import { useEffect, useState } from 'react'
import {
    ArrowSquareOutIcon,
    FloppyDiskIcon,
    FileXlsIcon,
    FileCsvIcon,
    ImageIcon,
    ShareIcon,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { Insight } from '../../../../Insights/Insight'
import ChartState from '../../../../Insights/ChartState'
import { Server } from '../../../../../api/Server'
import { useSelector } from 'react-redux'
import { useWorkspaceMetadata } from '../../../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { merge } from 'lodash'
import { ROUTES } from '../../../../../router/routes'
import { ScoopText, ScoopButton, ScoopRow } from '../../../../ui'
import { EVENTS, trackEvent } from '../../../../../events'
import styles from './ChartVisualization.module.scss'
import { useOnboarding, ONBOARDING_STEPS } from '../../../../../api/hooks/onboarding/useOnboarding'
import { useModal, MODALS } from '../../../../modals/'

export const ChartVisualization = ({
    visualization,
    isPublic,
    message,
    isEmbedded = false,
    handleExport,
    isLastVisualization = false,
    isTourWorkspace,
}) => {
    const navigate = useNavigate()
    const userID = useSelector((state) => state.auth.userID)
    const token = useSelector((state) => state.auth.token)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const [server] = useState(new Server(workspaceID, userID, token))
    const [loadingChart, setLoadingChart] = useState(true)

    // Modal Manager hooks
    const saveModal = useModal(MODALS.COMMON.SAVE)
    const onboardingCompleteModal = useModal(MODALS.ONBOARDING.COMPLETE)
    const [hasValidData, setHasValidData] = useState(false)
    const [config, setConfig] = useState(ChartState.getDefaultConfig())
    const [chartState, setChartState] = useState(new ChartState(server, config, setConfig))
    const { workspaceMetadata } = useWorkspaceMetadata()

    // Onboarding tracking
    const { completeStep, isStepCompleted } = useOnboarding()

    // Check if user is on step 2 of onboarding (completed step 1, but not step 2)
    const isOnboardingStep2 =
        !isPublic &&
        isStepCompleted(ONBOARDING_STEPS.ASK_QUESTION) &&
        !isStepCompleted(ONBOARDING_STEPS.OPEN_CHART)

    // Helper function to get icon for action button
    const getActionIcon = (iconName) => {
        const iconMap = {
            'file-excel': FileXlsIcon,
            'file-csv': FileCsvIcon,
            image: ImageIcon,
            share: ShareIcon,
        }
        const IconComponent = iconMap[iconName]
        return IconComponent ? <IconComponent size={16} /> : null
    }

    // Handler for footer action buttons
    const handleFooterAction = (action) => {
        console.log('Footer action clicked:', action)

        // Handle export actions
        if (action.actionId === 'export_excel') {
            handleExport('excel')
        } else if (action.actionId === 'export_csv') {
            handleExport('csv')
        }
    }

    const handleOpenInExplorer = () => {
        trackEvent(EVENTS.SCOOP_AI.OPEN_EXPLORER)
        // Complete onboarding step 2 (Open Chart) if not already completed
        if (!isPublic && !isStepCompleted(ONBOARDING_STEPS.OPEN_CHART)) {
            completeStep({ stepKey: ONBOARDING_STEPS.OPEN_CHART })
        }

        const newConfig = merge({}, ChartState.getDefaultConfig(), visualization)
        // Remove AI-generated properties that shouldn't be sent to Explorer
        delete newConfig.aiDescription
        delete newConfig.aiSummary
        delete newConfig.description
        delete newConfig.multiPassAnalysisType
        delete newConfig.multiPassEnhanced
        delete newConfig.followOnPrompts
        navigate(ROUTES.EXPLORER, {
            state: {
                editmodeConfig: newConfig,
                fromChat: true,
            },
        })
    }

    // Update chart state with workspace metadata
    useEffect(() => {
        if (workspaceMetadata) {
            setChartState((prevState) => {
                const cs = prevState
                cs.workspaceMetadata = workspaceMetadata
                return cs
            })
        }
    }, [workspaceMetadata])

    // Load chart data when visualization changes
    useEffect(() => {
        if (visualization && visualization.view) {
            setLoadingChart(true)
            setHasValidData(false)

            // Merge visualization config with default config
            const newConfig = merge(ChartState.getDefaultConfig(), visualization)

            // Apply custom title if provided
            if (visualization.chartTitle || visualization.title) {
                newConfig.styleOverrides = newConfig.styleOverrides || {}
                newConfig.styleOverrides.title = {
                    text: visualization.chartTitle || visualization.title,
                    textStyle: {
                        fontSize: 18,
                    },
                }
            }

            setConfig(newConfig)

            // Get chart results
            chartState.getResults(newConfig, () => {
                setLoadingChart(false)
                // Check if chart has valid data
                const hasData =
                    chartState.series?.length > 0 && chartState.series[0]?.data?.length > 0
                setHasValidData(hasData)
            })
        }
    }, [visualization])

    // Don't render anything if there's no visualization, it's loading
    if (!visualization || !visualization.view) return null

    return (
        <div className={styles.container}>
            {/* AI Summary if available */}
            {visualization.aiSummary && (
                <ScoopText className={styles.aiSummary}>{visualization.aiSummary}</ScoopText>
            )}

            {/* Chart Container */}
            <div
                className={`${styles.chartContainer} ${config.view === 'kpi' ? styles.kpi : styles.chart}`}
            >
                <Insight
                    chartProperties={chartState}
                    setChartProperties={setChartState}
                    config={config}
                    setConfig={setConfig}
                    server={server}
                    workspaceMetadata={workspaceMetadata}
                    clickable
                    chat
                />
            </div>

            {/* Action buttons - hide in public mode */}
            {!isPublic && (
                <ScoopRow className={styles.actionsContainer} justify="flex-end" gap={8}>
                    {/* Footer action buttons from API response - filtered based on mode */}
                    {/* Only show export buttons for the last visualization since backend exports the last query */}
                    {/* CSV export hidden until backend supports it */}
                    {/* Hide export buttons on tour workspace */}
                    {!isTourWorkspace &&
                        isLastVisualization &&
                        message?.footer?.actions
                            ?.filter((action) => {
                                if (isEmbedded) return true
                                // Only show Excel export for now - CSV not yet supported by backend
                                else return action.actionId === 'export_excel'
                            })
                            .map((action, index) => (
                                <ScoopButton
                                    key={index}
                                    size="xs"
                                    variant="outlineGray"
                                    onClick={() => handleFooterAction(action)}
                                    leftSection={getActionIcon(action.icon)}
                                    disabled={action.disabled}
                                >
                                    {action.text}
                                </ScoopButton>
                            ))}

                    {/* Original buttons - only show in native mode (not embedded) */}
                    {!isEmbedded && (
                        <>
                            {/* Save Summary - hidden on tour workspace */}
                            {!isTourWorkspace && (
                                <ScoopButton
                                    size="xs"
                                    variant="outlineGray"
                                    onClick={async () => {
                                        const result = await saveModal.show({
                                            insight: {
                                                insightName: '',
                                                insightKey: null,
                                                workspaceID: workspaceID,
                                                savedObject: JSON.stringify(
                                                    merge(
                                                        {},
                                                        ChartState.getDefaultConfig(),
                                                        visualization
                                                    )
                                                ),
                                            },
                                            workspaceMetadata,
                                        })
                                        // Show onboarding complete modal if this was the final step
                                        if (result?.onboardingJustCompleted) {
                                            // Small delay before showing modal to let the UI settle
                                            setTimeout(() => {
                                                onboardingCompleteModal.show({ isTourMode: false })
                                            }, 1000)
                                        }
                                    }}
                                    leftSection={<FloppyDiskIcon size={16} />}
                                >
                                    Save Summary
                                </ScoopButton>
                            )}
                            {/* Open in Explorer - always visible, glows during onboarding step 2 */}
                            <ScoopButton
                                size="xs"
                                variant="outlineGray"
                                onClick={handleOpenInExplorer}
                                leftSection={<ArrowSquareOutIcon size={16} />}
                                className={isOnboardingStep2 ? styles.glowButton : undefined}
                            >
                                Open in Explorer
                            </ScoopButton>
                        </>
                    )}
                </ScoopRow>
            )}
        </div>
    )
}
